import React, { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeOptions, ThemeOverride, } from '@wix/da-react-context/pkg/ThemeContext';
import EllipsisIcon from '../../../Icons/Ellipsis';
import { IconSize } from '../../../Icons/IconWrap';
import PopoverMenu from '../../../Menu/PopoverMenu';
import { MenuItem } from '../../../Menu';
import { actionMenuWidthMobile, actionMenuWidthDesktop } from '../../constants';
import { CommentContext } from '../CommentContext/CommentContext';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { BiEventType, BiDataHook } from '@wix/da-bi/pkg/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { isActionable } from '../../utils';
import s from './ActionsMenu.scss';
var ActionsMenu = function (_a) {
    var className = _a.className, itemClassName = _a.itemClassName, onHide = _a.onHide, onUnhide = _a.onUnhide, onReportSpam = _a.onReportSpam, onFeature = _a.onFeature, onUnfeature = _a.onUnfeature, onBlockUser = _a.onBlockUser, isCommentReduxReady = _a.isCommentReduxReady;
    var t = useTranslation().t;
    var isMobile = useContext(MobileContext);
    var biContext = useContext(BiContext);
    var logBiEvent = useBiLogger();
    var _b = useContext(CommentContext), setActionMenuOpen = _b.setActionMenuOpen, isActionMenuOpen = _b.isActionMenuOpen, comment = _b.comment;
    var isFeatured = comment.isFeatured, isHidden = comment.isHidden, isOwner = comment.isOwner, isAuthor = comment.isAuthor, isReportable = comment.isReportable;
    var onClickActionMenu = useCallback(function () {
        setActionMenuOpen(!isActionMenuOpen);
    }, [setActionMenuOpen, isActionMenuOpen]);
    var onToggleHide = useCallback(function () {
        if (!comment.isHidden) {
            onHide && onHide(comment, biContext.is_comment_thread === 1);
        }
        else {
            onUnhide && onUnhide(comment);
        }
    }, [onHide, onUnhide, comment, biContext]);
    var onSelectAction = useCallback(function (value) {
        var _a;
        logBiEvent({
            evid: BiEventType.COMMENT_ACTION,
            itemid: comment.itemId,
            typeid: comment.typeId,
            commentid: comment.commentId,
            commenterid: (_a = comment.user) === null || _a === void 0 ? void 0 : _a.useridUuid,
            menu_item_name: value,
        });
        switch (value) {
            case 'spam':
                // setTimeout allows actionmenu to be hidden before confirm prompt is shown
                setTimeout(function () {
                    ddt.log('comments', 'reporting spam', comment);
                    if (confirm('Report this comment as spam?') && onReportSpam) {
                        onReportSpam(comment);
                    }
                }, 100);
                break;
            case 'delete':
                ddt.log('comments', 'delete', comment);
                onToggleHide();
                break;
            case 'feature':
                if (onFeature && !isFeatured) {
                    onFeature(comment);
                }
                break;
            case 'unfeature':
                if (onUnfeature && isFeatured) {
                    ddt.log('comments', 'unfeaturing', comment);
                    onUnfeature(comment);
                }
                break;
            case 'hide':
                onToggleHide();
                break;
            case 'block':
                // setTimeout allows actionmenu to be hidden before confirm prompt is shown
                setTimeout(function () {
                    if (confirm('Add this user to your block list?') &&
                        onBlockUser &&
                        comment.user) {
                        onBlockUser(comment.user.userId);
                    }
                });
                break;
            default:
        }
        setActionMenuOpen(false);
    }, [
        comment,
        isFeatured,
        onReportSpam,
        onToggleHide,
        onUnfeature,
        onFeature,
        onBlockUser,
        setActionMenuOpen,
        logBiEvent,
    ]);
    if (!isActionable(comment)) {
        return null;
    }
    return (React.createElement("div", { className: classnames(s['actions-menu'], className), onClick: onClickActionMenu, "data-hook": BiDataHook.COMMENT_ACTION_MENU },
        React.createElement(ThemeOverride, { theme: ThemeOptions.DARK },
            React.createElement(PopoverMenu, { usePortal: true, placement: isMobile ? 'top-end' : 'top', isOpen: isActionMenuOpen, width: isMobile ? actionMenuWidthMobile : actionMenuWidthDesktop, onSelect: onSelectAction },
                isOwner && (React.createElement(MenuItem, { className: classnames(s['actions-menu-item'], itemClassName), value: isFeatured ? 'unfeature' : 'feature', dataHook: BiDataHook.COMMENT_FEATURE_BUTTON, disabled: !isCommentReduxReady },
                    isMobile && !isFeatured && t('comment.popover.feature.mobile'),
                    isMobile && isFeatured && t('comment.popover.unfeature.mobile'),
                    !isMobile && !isFeatured && t('comment.popover.feature'),
                    !isMobile && isFeatured && t('comment.popover.unfeature'))),
                (isOwner || isAuthor) && (React.createElement(MenuItem, { className: classnames(s['actions-menu-item'], itemClassName), value: "delete", dataHook: BiDataHook.COMMENT_DELETE_BUTTON, disabled: !isCommentReduxReady },
                    !isMobile && !isHidden && t('comment.popover.delete'),
                    isMobile && !isHidden && t('comment.popover.delete'))),
                isOwner && !isAuthor && (React.createElement(MenuItem, { className: classnames(s['actions-menu-item'], itemClassName), value: "block", dataHook: BiDataHook.COMMENT_BLOCK_BUTTON, disabled: !isCommentReduxReady },
                    !isMobile && t('comment.popover.block'),
                    isMobile && t('comment.popover.block.mobile'))),
                isReportable && !isAuthor && (React.createElement(MenuItem, { className: classnames(s['actions-menu-item'], itemClassName), value: "spam", dataHook: BiDataHook.COMMENT_SPAM_BUTTON, disabled: !isCommentReduxReady },
                    !isMobile && t('comment.popover.report'),
                    isMobile && t('comment.popover.report.mobile'))))),
        React.createElement(EllipsisIcon, { size: IconSize.MEDIUM, disableDefaultColors: true })));
};
ActionsMenu.displayName = 'ActionsMenu';
export default ActionsMenu;
//# sourceMappingURL=ActionsMenu.js.map